import { FC, useState } from 'react';
import { Button, Modal, Input } from '@salecycle/ui-component-library';
import { useTranslation } from 'react-i18next';
import { segmentNameValidation } from '../progress';
import { useLocation } from 'react-router-dom';
import {
  useOffsiteCampaignEditorDispatch,
  useOffsiteCampaignEditorState,
} from '../../context/UseOffsiteCampaignEditorContext';
import { uuid } from 'utils';
import { Site } from 'sections/client-management/entities';
import { ICycle, IPercentageSplit, ISegment } from '../../../entities/offsiteCampaign';

export const NewSegment: FC = () => {
  const { t } = useTranslation('campaigns');
  const dispatch = useOffsiteCampaignEditorDispatch();
  const location = useLocation<{ showAddNewSegmentModal: boolean; addDefaultSegment: boolean }>();
  const showAddNewSegmentModal =
    location.state === undefined || location.state.showAddNewSegmentModal === undefined
      ? false
      : location.state.showAddNewSegmentModal;

  const { site } = useOffsiteCampaignEditorState();

  const [isOpen, setIsOpen] = useState(showAddNewSegmentModal);

  const [segmentName, setSegmentName] = useState<string>('');

  const onAddSegmentClick = () => {
    setIsOpen(!isOpen);
  };

  const handleSegmentNameChange = (value: string) => setSegmentName(value);

  const isSegmentNameValid = segmentNameValidation(segmentName, t).expression;

  const handleOkClick = () => {
    setIsOpen(false);
    const newSegment = getNewSegment();
    setSegmentName('');
    dispatch({
      type: 'ADD_SEGMENT',
      payload: {
        segment: newSegment,
      },
    });
  };

  function getNewSegment(): ISegment {
    return {
      id: uuid(),
      name: segmentName,
      type: 'all',
      segmentFilters: [],
      percentageSplits: getPercentageSplits(site),
    };
  }

  function getPercentageSplits(site: Site): IPercentageSplit[] {
    const split: IPercentageSplit = {
      id: uuid(),
      percentage: 100,
      name: 'Default',
      cycles: getCycle(site),
    };
    return [split];
  }

  function getCycle(site: Site): ICycle[] {
    const cycle: ICycle = {
      templateId: null,
      messageType: 'email',
      delay: site?.sessionTime ? site.sessionTime : 0,
      id: uuid(),
      subjectLineId: undefined,
    };
    return [cycle];
  }

  return (
    <>
      <Button variation="secondaryDark" id="addSegment-button" onClick={onAddSegmentClick}>
        {t('campaigns:offsiteEditor.segments.addSegment.label')}
      </Button>
      <Modal
        heading={t('campaigns:offsiteEditor.segments.addSegment.modal.heading')}
        isOpen={isOpen}
        onCloseButtonClicked={() => setIsOpen(!isOpen)}
        minHeight={250}
        onOkButtonClicked={handleOkClick}
        okButtonDisabled={!isSegmentNameValid}
        okButtonLabel={t('campaigns:offsiteEditor.segments.addSegment.modal.addButton')}
        variation="clean"
        overflow="visible"
      >
        <Input
          label={t('campaigns:offsiteEditor.segments.addSegment.modal.segmentName')}
          ariaLabel={t('campaigns:offsiteEditor.segments.addSegment.modal.segmentName')}
          value={segmentName}
          onChange={handleSegmentNameChange}
          placeholder={t('campaigns:offsiteEditor.segments.addSegment.modal.segmentNamePlaceholder')}
          validationMessage={segmentName && !isSegmentNameValid && segmentNameValidation(segmentName, t).message}
          validationState={segmentName && !isSegmentNameValid && 'warning'}
        />
      </Modal>
    </>
  );
};
